import * as angular from 'angular';
import * as _ from 'underscore';
import { RetailerService } from '../api/retailer.service';
import { HttpService } from '../../../app.common/modules/api/http.service';
import { BlobService } from '../../../app.common/modules/common/services/blob.service';
import { PopupService } from '../../../app.common/modules/common/services/popup.service';
import {
    InformationCardsPagingResult, InformationCardFilter, InformationCard, InformationCardLink,
    EnumListItem
} from '../core';
import {
    IInformationCard, ILinkListItem,
    IInformationCardLink, IColumnProductSortSet
} from './models';
import { IRetailerRootScope } from '../core/models';

import { IImgFileModel } from '../../../app.common/modules/common/directives/models';
import { IInformationCardsSearch } from '../model/IInformationCardsSearch';

export class InformationCardsService {
    static $inject = ['$rootScope', 'http', '$q', '$uibModal', 'retailerService', 'blobService', 'popupService', '$translate'];

    importType = {
        product: 1,
        category: 2
    };
    private asc = 'ascending';
    private desc = 'descending';
    private sortSet: IColumnProductSortSet[] = [
        {
            column: 'CreateDate',
            order: this.asc,
            title: this.$translate.instant('create_date'),
            classSort: 'amount-asc'
        }, {
            column: 'CreateDate',
            order: this.desc,
            title: this.$translate.instant('create_date'),
            classSort: 'amount-desc'
        }, {
            column: 'Title',
            order: this.asc,
            title: this.$translate.instant('information_card_name'),
            classSort: 'alpha-asc'
        }, {
            column: 'Title',
            order: this.desc,
            title: this.$translate.instant('information_card_name'),
            classSort: 'alpha-desc'
        }
    ];

    private linkViewClasses = {
        video: 'fa-youtube-play',
        document: 'fa-file-pdf-o',
        website: 'fa-globe',
        facebook: 'fa-facebook-official',
        twitter: 'fa-twitter',
        instagram: 'fa-instagram',
        pinterest: 'fa-pinterest',
        default: 'fa-external-link-square'
    };

    private linkTypes: EnumListItem[] = null;

    private defaultProduct = <IInformationCard>{
        title: '',
        description: '',
        links: [],
        imagesID: [],
        error: {},
        nfcStickers: []
    };

    constructor(
        private $rootScope: IRetailerRootScope,
        private http: HttpService,
        private $q: ng.IQService,
        private $uibModal: ng.ui.bootstrap.IModalService,
        private retailerService: RetailerService,
        private blobService: BlobService,
        private popupService: PopupService,
        private $translate: angular.translate.ITranslateService
    ) {

    }

    getDefaultProductModel() {
        var product = angular.copy(this.defaultProduct);
        return this.$q.when(product);
    }

    getInformationCards(filter: InformationCardFilter) {
        return this.http.get<InformationCardsPagingResult>(this.retailerService.getInformationCards(), { params: filter });
    }

    searchInformationCards(filter: IInformationCardsSearch) {
        return this.http.get<any>(this.retailerService.searchInformationCards(), { params: filter });
    }

    getImageUrl(imageID: string, width?: number, height?: number): string {
        return this.retailerService.getInformationCardImageUrl(imageID, width, height || width);
    }

    getSort(column?: string, order?: string): IColumnProductSortSet {
        if (column && order) {
            return _.findWhere(this.sortSet, { column: column, order: order });
        } else {
            return this.sortSet[1];
        }
    }

    getSortSet(): IColumnProductSortSet[] {
        return this.sortSet;
    }

    uploadImage(file: IImgFileModel) {
        if (!file) {
            return this.$q.when(<string>null);
        }
        var fd = new FormData();
        fd.append('FileImage', this.blobService.dataUriToBlob(file.dataUri), file.name);
        return this.http.post<string>(this.retailerService.informationCardImage(), fd, {
            transformRequest: angular.identity,
            headers: { 'Content-Type': undefined }
        });
    }

    deleteImage(imageID: string) {
        return this.http.delete<boolean>(this.retailerService.informationCardImage(imageID));
    }

    copyImage(imagesID: string[]) {
        return this.http.post<string[]>(this.retailerService.informationCardImageCopy(), imagesID);
    }

    updateInformationCard(cardID: string, model: IInformationCard) {
        return this.http.put(this.retailerService.informationCard(cardID), this.toServerInformationCardModel(model));
    }

    createInformationCard(model: IInformationCard) {
        return this.http.post(this.retailerService.informationCard(), this.toServerInformationCardModel(model));
    }

    archiveInformationCard(cardID: string) {
        return this.http.post<string>(this.retailerService.archiveInformationCard(cardID), null);
    }

    restoreInformationCard(cardID: string) {
        return this.http.post<string>(this.retailerService.restoreInformationCard(cardID), null);
    }

    getLinkTypes = async () => {
        if (_.isArray(this.linkTypes)) {
            return this.$q.when(this.linkTypes);
        } else {
            let types = <ILinkListItem[]>await this.http.get<EnumListItem[]>(this.retailerService.informationCardLinkTypes());
            _.forEach(types, (type) => {
                var viewClass = this.linkViewClasses[type.Name.toLowerCase()];
                type.viewClass = viewClass ? viewClass : this.linkViewClasses.default;
            });
            this.linkTypes = types;
            return this.$q.when(this.linkTypes);
        }
    }

    isExistInformationCards(state: number) {
        return this.http.get<boolean>(this.retailerService.existInformationCards(state));
    }

    showSaveChangesDialog() {
        var title = this.$translate.instant('save_changes_in_a_product_copy');
        var message = this.$translate.instant('save_changes_in_a_product_copy_confirm');
        return this.popupService.confirm({
            title: title,
            content: message
        });
    }

    getInformationCardModel = async (productID: string, edit: boolean) => {

        let serverModel = await this.http.get<InformationCard>(this.retailerService.informationCard(productID));
        let imagesID = <string[]>[];
        if (!edit) {
            _.forEach(serverModel.ImagesID, (id) => { imagesID.push(id); });
        }
        if (_.isEmpty(imagesID)) {
            return this.toClientInformationCardModel(serverModel, imagesID, edit);
        } else {
            let exchange = await this.copyImage(imagesID);
            return this.toClientInformationCardModel(serverModel, exchange, edit);
        }
    }

    toServerInformationCardModel(model: IInformationCard): InformationCard {
        return {
            Title: model.title,
            NfcStickers: model.nfcStickers,
            Description: model.description,
            Links: _.map(model.links, this.toServerLink),
            ImagesID: model.imagesID,
        };
    }

    uploadImportFile(file: File, importType: number) {
        if (!file) {
            return this.$q.when(null);
        }
        let url = importType === this.importType.category ? this.retailerService.categoryMappingImport() : this.retailerService.productsImport();
        var fd = new FormData();
        fd.append('file', file, file.name);
        return this.http.post<string>(url, fd, {
            transformRequest: angular.identity,
            headers: { 'Content-Type': undefined }
        });
    }

    private toClientInformationCardModel(serverProduct: InformationCard, exchangeImages: string[], edit: boolean): IInformationCard {
        var product = angular.copy(this.defaultProduct);
        product.title = serverProduct.Title;
        product.nfcStickers = serverProduct.NfcStickers;
        product.description = serverProduct.Description;
        product.draft = serverProduct.Draft;
        product.links = <IInformationCardLink[]>serverProduct.Links;
        product.imagesID = edit ? serverProduct.ImagesID : _.map(serverProduct.ImagesID, (id) => { return exchangeImages[id]; });
        return product;
    }

    private toServerLink(link: IInformationCardLink): InformationCardLink {
        return {
            Type: <InformationCardLink.TypeEnum>link.type.ID,
            Title: link.Title,
            Url: link.Url
        };
    }
}
angular
    .module('naut')
    .service('informationCardsService', InformationCardsService);



// WEBPACK FOOTER //
// ./~/tslint-loader!./src/app.retailer/modules/informationCards/informationCards.service.ts